import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { MAX_WHITELABEL_HOSTS, saasAuthResellerApi } from '@/api/saasAuthReseller';
import { emptyDomain, type WhitelabelDomainDto } from '@/api/saasAuthTypes';
import { useToast } from '@/components/useToast';
import { useTenant } from '@/tenant/useTenant';
import { templateToHosts } from './domainState';

/** Local-only code for the host-cap refusal — never a server envelope code. */
export const TOO_MANY_HOSTS = 'TOO_MANY_HOSTS_LOCAL';

export type DomainLoadStatus = 'loading' | 'ready' | 'forbidden' | 'error';

export interface WhitelabelDomainState {
  status: DomainLoadStatus;
  dto: WhitelabelDomainDto;
  error: ApiError | null;
  mutationError: ApiError | null;
  saving: boolean;
  verifying: boolean;
  removing: boolean;
  reload: () => void;
  save: (domain: string, appPrefix: string) => Promise<void>;
  verify: () => Promise<void>;
  remove: () => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

export function useWhitelabelDomain(): WhitelabelDomainState {
  const { t } = useTranslation();
  const showToast = useToast();
  // saas-auth's per-tenant routes take the numeric tenant id, not the slug (H §5).
  const tenantId = useTenant().id;
  const [status, setStatus] = useState<DomainLoadStatus>('loading');
  const [dto, setDto] = useState<WhitelabelDomainDto>(emptyDomain);
  const [error, setError] = useState<ApiError | null>(null);
  const [mutationError, setMutationError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const started = useRef(false);

  const load = useCallback(() => {
    setStatus('loading');
    setError(null);
    saasAuthResellerApi.getDomain(tenantId).then(
      (next) => {
        setDto(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        const mapped = toApiError(err);
        if (mapped.code === 'FORBIDDEN' || mapped.status === 403) {
          setDto(emptyDomain());
          setError(mapped);
          setStatus('forbidden');
          return;
        }
        setDto(emptyDomain());
        setError(mapped);
        setStatus('error');
      },
    );
  }, [tenantId]);

  const refetch = useCallback(async () => {
    const next = await saasAuthResellerApi.getDomain(tenantId);
    setDto(next);
    setError(null);
    setStatus('ready');
  }, [tenantId]);

  const save = useCallback(
    async (domain: string, appPrefix: string) => {
      setSaving(true);
      setMutationError(null);
      try {
        const template = await saasAuthResellerApi.getDomainTemplate(tenantId);
        const hosts = templateToHosts(template, appPrefix);
        // saas-auth refuses more than 10 hosts (SASATH-0342); the template gives fewer — never send more.
        if (hosts.length > MAX_WHITELABEL_HOSTS) {
          throw new ApiError('VALIDATION', 400, {
            details: { code: TOO_MANY_HOSTS },
          });
        }
        await saasAuthResellerApi.putDomain(tenantId, { domain, hosts });
        await refetch();
        showToast(t('domain.toast.saved'));
      } catch (err) {
        const mapped = toApiError(err);
        setMutationError(mapped);
        throw mapped;
      } finally {
        setSaving(false);
      }
    },
    [refetch, showToast, t, tenantId],
  );

  const verify = useCallback(async () => {
    setVerifying(true);
    setMutationError(null);
    try {
      const result = await saasAuthResellerApi.verifyDomain(tenantId);
      await refetch();
      showToast(t(result.allVerified ? 'domain.toast.verified' : 'domain.toast.verifyPending'));
    } catch (err) {
      const mapped = toApiError(err);
      setMutationError(mapped);
      throw mapped;
    } finally {
      setVerifying(false);
    }
  }, [refetch, showToast, t, tenantId]);

  const remove = useCallback(async () => {
    setRemoving(true);
    setMutationError(null);
    try {
      await saasAuthResellerApi.removeDomain(tenantId);
      await refetch();
      showToast(t('domain.toast.removed'));
    } catch (err) {
      const mapped = toApiError(err);
      setMutationError(mapped);
      throw mapped;
    } finally {
      setRemoving(false);
    }
  }, [refetch, showToast, t, tenantId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  return {
    status,
    dto,
    error,
    mutationError,
    saving,
    verifying,
    removing,
    reload: load,
    save,
    verify,
    remove,
  };
}
